import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { getSuppliers, getBranches, getProducts, createPurchaseOrder } from "../services/api";
import { ICONS } from "../icons";
import Button from "../components/UI/Button";
import FloatingInput from "../components/UI/FloatingInput";
import SelectBox from "../components/UI/SelectBox";
import { notify } from "../utils/notify";
import PageHeader from "../components/UI/PageHeader";
import { useTranslation } from "../context/I18nContext";

function money(n) {
  return Number(n || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

// A brand-new PurchaseOrder doesn't exist yet until the whole form is
// submitted — unlike AddEditProduct.js's Quantity Discount tiers (each
// persisted immediately, one API call per row, only possible once the
// product itself already has an id), every line item here lives purely in
// local React state and is sent as one array in the single POST that
// creates the order (see purchaseOrderPayload.Items on the backend).
export default function AddPurchaseOrder() {
  const navigate = useNavigate();
  const token = localStorage.getItem("token");
  const { t } = useTranslation("purchase_orders");
  const { t: tCommon } = useTranslation("common");

  const [suppliers, setSuppliers] = useState([]);
  const [branches, setBranches] = useState([]);
  const [products, setProducts] = useState([]);

  const [supplierId, setSupplierId] = useState("");
  const [branchId, setBranchId] = useState("");
  const [notes, setNotes] = useState("");
  const [items, setItems] = useState([]);

  const [draftProductId, setDraftProductId] = useState("");
  const [draftQuantity, setDraftQuantity] = useState("");
  const [draftUnitCost, setDraftUnitCost] = useState("");

  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});

  // Defense-in-depth against a fast double-click on "Create Purchase Order"
  // firing two near-simultaneous submits before React commits the Button
  // component's own disabled-while-loading state — the actual crash this
  // could once cause (system_settings' idx_category_key duplicate-key
  // error) is already fixed server-side in nextSequenceCounter, but this
  // still stops a double-click from silently creating two identical POs.
  const submittingRef = useRef(false);

  useEffect(() => {
    getSuppliers(token).then(setSuppliers).catch(() => notify.error(t("notify_load_suppliers_failed", "Failed to load suppliers")));
    getBranches(token).then(setBranches).catch(() => notify.error(t("notify_load_branches_failed", "Failed to load branches")));
    getProducts(token).then(setProducts).catch(() => notify.error(t("notify_load_products_failed", "Failed to load products")));
  }, [token]); // eslint-disable-line react-hooks/exhaustive-deps

  const supplierOptions = suppliers.map((s) => ({ value: String(s.id), label: s.name }));
  const branchOptions = branches.map((b) => ({ value: String(b.id), label: b.name }));
  const productOptions = products.map((p) => ({ value: String(p.id), label: `${p.name} (${p.sku})` }));

  const addItem = () => {
    if (!draftProductId) {
      notify.error(t("validation_item_product_required", "Pick a product for every line item"));
      return;
    }
    const qty = Number(draftQuantity);
    if (!qty || qty <= 0) {
      notify.error(t("validation_item_quantity_required", "Every line item needs a quantity greater than zero"));
      return;
    }
    const product = products.find((p) => String(p.id) === draftProductId);
    const unitCost = draftUnitCost === "" ? (product?.cost || 0) : Number(draftUnitCost);

    setItems((prev) => [
      ...prev,
      {
        key: `${draftProductId}-${Date.now()}`,
        product_id: Number(draftProductId),
        product_name: product?.name || "",
        product_sku: product?.sku || "",
        quantity: qty,
        unit_cost: unitCost,
      },
    ]);
    setDraftProductId("");
    setDraftQuantity("");
    setDraftUnitCost("");
  };

  const removeItem = (key) => setItems((prev) => prev.filter((i) => i.key !== key));

  const total = items.reduce((sum, i) => sum + i.quantity * i.unit_cost, 0);

  const handleSubmit = async () => {
    if (submittingRef.current) return;

    setErrors({});
    const newErrors = {};
    if (!supplierId) newErrors.supplier = t("validation_supplier_required", "Supplier is required");
    if (!branchId) newErrors.branch = t("validation_branch_required", "Branch is required");
    if (items.length === 0) newErrors.items = t("validation_items_required", "Add at least one line item");

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      notify.error(tCommon("validation_fill_required_fields", "Please fill in the required fields correctly"));
      return;
    }

    submittingRef.current = true;
    setLoading(true);
    const payload = {
      supplier_id: Number(supplierId),
      branch_id: Number(branchId),
      notes: notes.trim(),
      items: items.map((i) => ({ product_id: i.product_id, quantity: i.quantity, unit_cost: i.unit_cost })),
    };

    try {
      const created = await createPurchaseOrder(token, payload);
      notify.success(t("notify_created", "Purchase order created successfully"));
      navigate(`/purchase-orders/${created.id}`);
    } catch (err) {
      notify.error(err.data?.error || err.message || tCommon("error_generic", "Something went wrong"));
    } finally {
      submittingRef.current = false;
      setLoading(false);
    }
  };

  return (
    <div className="p-4 md:p-8 max-w-3xl mx-auto">
      <PageHeader className="flex items-center gap-4 mb-8 border-b border-gray-100 pb-6">
        <button onClick={() => navigate("/purchase-orders")} className="p-2 hover:bg-gray-100 rounded-xl transition-colors text-gray-400">
          <ICONS.ChevronLeft className="w-6 h-6 rtl:rotate-180" />
        </button>
        <div>
          <h2 className="text-2xl font-black text-gray-800 tracking-tight">{t("add_title", "New Purchase Order")}</h2>
          <p className="text-gray-400 text-sm italic font-medium">{t("add_subtitle", "Pick a supplier and branch, then add the products you're ordering")}</p>
        </div>
      </PageHeader>

      <div className="bg-white p-6 md:p-8 rounded-[2.5rem] border border-gray-100 shadow-sm">
        <div className="space-y-6">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <SelectBox label={t("field_supplier_label", "Supplier")} options={supplierOptions} value={supplierId} onChange={setSupplierId} error={errors.supplier} icon={ICONS.Suppliers} />
            <SelectBox label={t("field_branch_label", "Branch (where stock will arrive)")} options={branchOptions} value={branchId} onChange={setBranchId} error={errors.branch} icon={ICONS.Branches} />
          </div>

          <FloatingInput label={t("field_notes_label", "Notes (optional)")} value={notes} onChange={(e) => setNotes(e.target.value)} />

          <div className="pt-4 border-t border-gray-50">
            <h3 className="text-sm font-bold text-gray-700 mb-3">{t("items_section_title", "Line Items")}</h3>

            <div className="grid grid-cols-1 sm:grid-cols-[1fr_120px_140px_auto] gap-3 items-end mb-4">
              <SelectBox label={t("field_product_label", "Product")} options={productOptions} value={draftProductId} onChange={setDraftProductId} />
              <FloatingInput label={t("field_quantity_label", "Quantity")} type="number" min="0" value={draftQuantity} onChange={(e) => setDraftQuantity(e.target.value)} />
              <FloatingInput label={t("field_unit_cost_label", "Unit Cost")} type="number" min="0" step="0.01" value={draftUnitCost} onChange={(e) => setDraftUnitCost(e.target.value)} />
              <Button variant="secondary" onClick={addItem} className="h-[56px]">{t("btn_add_item", "+ Add item")}</Button>
            </div>

            {errors.items && <p className="text-[10px] text-red-500 font-bold mb-3 ms-2 uppercase tracking-tight">{errors.items}</p>}

            {items.length === 0 ? (
              <p className="text-gray-400 text-sm italic">{t("items_empty", "No line items yet — add at least one product above.")}</p>
            ) : (
              <div className="space-y-2">
                {items.map((item) => (
                  <div key={item.key} className="flex items-center justify-between gap-3 bg-gray-50 rounded-xl px-4 py-3">
                    <div className="flex-grow">
                      <p className="font-bold text-gray-800 text-sm">{item.product_name}</p>
                      <p className="text-xs text-gray-400 font-mono">{item.product_sku}</p>
                    </div>
                    <div className="text-xs text-gray-500 font-mono">{item.quantity} × {money(item.unit_cost)}</div>
                    <div className="font-bold text-gray-800 font-mono w-24 text-end">{money(item.quantity * item.unit_cost)}</div>
                    <button type="button" onClick={() => removeItem(item.key)} className="p-1.5 text-gray-400 hover:text-red-500 transition-colors">
                      <ICONS.Close className="w-5 h-5" />
                    </button>
                  </div>
                ))}
                <div className="flex justify-end pt-2">
                  <div className="text-sm">
                    <span className="text-gray-400 font-bold uppercase text-xs me-2">{t("total_label", "Total")}</span>
                    <span className="text-gray-800 font-black font-mono text-lg">{money(total)}</span>
                  </div>
                </div>
              </div>
            )}
          </div>

          <div className="flex gap-4 pt-6 border-t border-gray-50">
            <Button
              variant="primary"
              onClick={handleSubmit}
              loading={loading}
              className="flex-grow shadow-lg shadow-blue-500/10"
              icon={ICONS.Plus}
            >
              {t("btn_create_purchase_order", "Create Purchase Order")}
            </Button>
            <Button variant="secondary" onClick={() => navigate("/purchase-orders")}>{tCommon("btn_cancel", "Cancel")}</Button>
          </div>
        </div>
      </div>
    </div>
  );
}
